import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { 
  Moon, Sun, Clock, Heart, Utensils, Baby, Zap, Sparkles, Star, Cloud,
  ChevronDown, ChevronUp, Calendar, CheckCircle2, Loader2, FileText
} from 'lucide-react';
import apiService, { convertReportToFrontend } from '../services/api';

// Mapeamento de ícones
const iconMap = {
  Sun, Moon, Clock, Heart, Utensils, Baby, Zap, Sparkles, Star, Cloud
};

// Helper para garantir contraste de cores
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#6B5B8C';
  
  // Converter hex para RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Calcular luminância
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Se a cor for muito clara (luminância > 0.6), escurecer
  if (luminance > 0.6) {
    // Escurecer a cor em 40%
    const darken = 0.6;
    const newR = Math.floor(r * darken);
    const newG = Math.floor(g * darken);
    const newB = Math.floor(b * darken);
    return `rgb(${newR}, ${newG}, ${newB})`;
  }
  
  return hexColor;
};

// Animações
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { 
    opacity: 1, 
    scale: 1,
    transition: { type: "spring", stiffness: 200, damping: 20 }
  }
};

const SharedPlanPage = () => {
  const { reportId } = useParams();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [activeSection, setActiveSection] = useState('pillars');
  const [showWelcome, setShowWelcome] = useState(true);
  const [expandedSummary, setExpandedSummary] = useState(false);

  useEffect(() => {
    const loadPlan = async () => {
      try {
        setLoading(true);
        const data = await apiService.getReport(reportId);
        
        if (!data.sleep_plan) {
          setError('O plano ainda não está disponível.');
          return;
        }
        
        if (data.status !== 'approved' && data.status !== 'pending_approval') {
          setError('O plano ainda está em preparação.');
          return;
        }
        
        setPlan({
          babyName: data.sleep_plan.baby_name || data.baby_name,
          babyAge: data.sleep_plan.baby_age || data.baby_age,
          ...data.sleep_plan
        });
        
        // Esconder welcome após 3 segundos
        setTimeout(() => setShowWelcome(false), 3000);
        
      } catch (err) {
        console.error('Erro ao carregar plano:', err);
        setError('Não foi possível carregar o plano.');
      } finally {
        setLoading(false);
      }
    };
    
    loadPlan();
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-[#FAE0D5] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <Moon className="w-12 h-12 text-[#8F7AC5] mx-auto" />
          </motion.div>
          <p className="mt-4 text-[#6B5B8C] font-medium">Carregando seu plano...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-[#FAE0D5] flex items-center justify-center p-6">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 shadow-lg max-w-md text-center"
        >
          <Moon className="w-16 h-16 text-[#D98DAE] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#30244F] mb-2">Ops!</h2>
          <p className="text-[#6B5B8C]">{error}</p>
        </motion.div>
      </div>
    );
  }

  const sections = [
    { id: 'pillars', label: 'Pilares', icon: Star },
    { id: 'routine', label: 'Rotina', icon: Clock },
    { id: 'priorities', label: 'Semanas', icon: Calendar },
  ];

  const IconComponent = (iconName) => {
    return iconMap[iconName] || Sparkles;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FBF4FF] to-white">
      {/* Welcome Overlay */}
      <AnimatePresence>
        {showWelcome && (
          <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="fixed inset-0 z-50 bg-gradient-to-b from-[#8F7AC5] to-[#D98DAE] flex items-center justify-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{ duration: 0.5 }}
              className="text-center text-white px-8"
            >
              <motion.div
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Moon className="w-20 h-20 mx-auto mb-6" />
              </motion.div>
              <h1 className="text-3xl font-bold mb-2">Plano de Sono</h1>
              <p className="text-xl opacity-90">{plan?.babyName}</p>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ duration: 2.5, ease: "easeInOut" }}
                className="h-1 bg-white/30 rounded-full mt-6 mx-auto max-w-[200px]"
              >
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 2.5, ease: "easeInOut" }}
                  className="h-full bg-white rounded-full"
                />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <motion.header 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-[#FAE0D5]"
      >
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#8F7AC5] to-[#D98DAE] flex items-center justify-center">
                <Moon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-[#30244F]">{plan?.babyName}</h1>
                <p className="text-xs text-[#6B5B8C]">{plan?.babyAge} meses</p>
              </div>
            </div>
          </div>
        </div>
      </motion.header>

      {/* Navigation Tabs */}
      <motion.nav 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="sticky top-[73px] z-30 bg-white border-b border-[#FAE0D5]"
      >
        <div className="max-w-lg mx-auto px-4">
          <div className="flex gap-1">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex-1 py-3 px-2 text-sm font-medium transition-all duration-300 relative ${
                    activeSection === section.id 
                      ? 'text-[#8F7AC5]' 
                      : 'text-[#9890A8] hover:text-[#6B5B8C]'
                  }`}
                >
                  <div className="flex items-center justify-center gap-1.5">
                    <Icon className="w-4 h-4" />
                    <span>{section.label}</span>
                  </div>
                  {activeSection === section.id && (
                    <motion.div
                      layoutId="activeTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE]"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </motion.nav>

      {/* Content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        <AnimatePresence mode="wait">
          {/* Pillars Section */}
          {activeSection === 'pillars' && (
            <motion.div
              key="pillars"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, x: -50 }}
            >
              {/* Summary Card */}
              <motion.div
                variants={fadeInUp}
                className="bg-gradient-to-br from-[#FAE0D5] to-white rounded-2xl p-5 mb-6 shadow-sm"
              >
                <h3 className="text-base font-bold text-[#8F7AC5] mb-3 flex items-center gap-2">
                  <Sparkles className="w-5 h-5" />
                  Análise Personalizada
                </h3>
                <p className="text-base text-[#6B5B8C] leading-relaxed font-medium whitespace-pre-wrap">
                  {expandedSummary || !plan?.summary || plan?.summary?.length <= 250
                    ? plan?.summary
                    : `${plan?.summary?.substring(0, 250)}...`
                  }
                </p>
                {plan?.summary && plan?.summary?.length > 250 && (
                  <button
                    onClick={() => setExpandedSummary(!expandedSummary)}
                    className="mt-3 text-[#8F7AC5] font-semibold text-sm flex items-center gap-1 hover:text-[#30244F] transition-colors"
                  >
                    {expandedSummary ? (
                      <>
                        <ChevronUp className="w-4 h-4" />
                        Ver menos
                      </>
                    ) : (
                      <>
                        <ChevronDown className="w-4 h-4" />
                        Ver mais
                      </>
                    )}
                  </button>
                )}
              </motion.div>

              {/* Pillars */}
              <h2 className="text-xl font-bold text-[#30244F] mb-4">Os Pilares do Sono</h2>
              
              {plan?.pillars?.map((pillar, index) => {
                const Icon = IconComponent(pillar.icon);
                const isExpanded = expandedPillar === pillar.id;
                const items = pillar.instructions || pillar.meals || pillar.signals || [];
                const hasContent = items.length > 0;
                
                return (
                  <motion.div
                    key={pillar.id}
                    variants={scaleIn}
                    className="mb-4"
                  >
                    <motion.button
                      onClick={() => setExpandedPillar(isExpanded ? null : pillar.id)}
                      className={`w-full bg-white rounded-2xl p-5 shadow-sm border text-left transition-all hover:shadow-md ${
                        !isExpanded && hasContent ? 'border-[#D98DAE]/50' : 'border-[#FAE0D5]'
                      }`}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="flex items-start gap-4">
                        <motion.div 
                          className="w-14 h-14 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${pillar.color}20` }}
                          whileHover={{ scale: 1.1, rotate: 5 }}
                        >
                          <Icon className="w-7 h-7" style={{ color: pillar.color }} />
                        </motion.div>
                        <div className="flex-1">
                          <h3 className="font-extrabold text-[#30244F] text-lg">{pillar.title}</h3>
                          {pillar.mainValue && (
                            <p 
                              className="text-sm font-semibold mt-1" 
                              style={{ color: getContrastColor(pillar.color) }}
                            >
                              {pillar.mainValue}
                            </p>
                          )}
                          {/* Preview do conteúdo quando fechado */}
                          {!isExpanded && hasContent && (
                            <div className="mt-2">
                              <p className="text-sm text-[#6B5B8C] line-clamp-1 italic">
                                "{items[0]?.substring(0, 50)}{items[0]?.length > 50 ? '...' : ''}"
                              </p>
                              <p className="text-xs text-[#D98DAE] font-semibold mt-1 flex items-center gap-1">
                                <span className="inline-block w-1.5 h-1.5 bg-[#D98DAE] rounded-full animate-pulse"></span>
                                Toque para ver {items.length} {items.length === 1 ? 'dica' : 'dicas'}
                              </p>
                            </div>
                          )}
                        </div>
                        {hasContent && (
                          <motion.div
                            animate={{ 
                              rotate: isExpanded ? 180 : 0,
                              y: isExpanded ? 0 : [0, -3, 0]
                            }}
                            transition={{ 
                              rotate: { duration: 0.3 },
                              y: { duration: 1.5, repeat: isExpanded ? 0 : Infinity, ease: "easeInOut" }
                            }}
                            className={`p-1 rounded-full ${!isExpanded ? 'bg-[#D98DAE]/20' : ''}`}
                          >
                            <ChevronDown 
                              className={`w-6 h-6 ${!isExpanded ? 'text-[#D98DAE]' : 'text-[#9890A8]'}`} 
                            />
                          </motion.div>
                        )}
                      </div>
                    </motion.button>
                    
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="bg-white/50 rounded-b-2xl px-5 pb-5 pt-3 border-x border-b border-[#FAE0D5] -mt-2">
                            {items.map((item, idx) => (
                              <motion.div
                                key={idx}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                                className="flex items-start gap-3 py-2.5"
                              >
                                <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: pillar.color }} />
                                <span className="text-base font-medium leading-relaxed text-[#30244F]">
                                  {item}
                                </span>
                              </motion.div>
                            ))}
                            {pillar.note && (
                              <motion.p
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                className="text-sm text-[#D98DAE] font-semibold italic mt-3 pl-8 border-l-2 border-[#D98DAE] py-1"
                              >
                                {pillar.note}
                              </motion.p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
              
              {/* Free Texts - Informações Adicionais */}
              {plan?.freeTexts && plan.freeTexts.length > 0 && (
                <>
                  <h2 className="text-xl font-bold text-[#30244F] mb-4 mt-8">Informações Adicionais</h2>
                  {plan.freeTexts.map((freeText, index) => (
                    <motion.div
                      key={freeText.id || index}
                      variants={fadeInUp}
                      className="mb-4 rounded-2xl p-5 shadow-sm"
                      style={{ 
                        backgroundColor: `${freeText.color}15`,
                        borderLeft: `4px solid ${freeText.color}`
                      }}
                    >
                      <h3 
                        className="font-bold text-lg mb-3 flex items-center gap-2"
                        style={{ color: freeText.color }}
                      >
                        <FileText className="w-5 h-5" />
                        {freeText.title}
                      </h3>
                      <p className="text-base text-[#30244F] leading-relaxed whitespace-pre-wrap font-medium">
                        {freeText.content}
                      </p>
                    </motion.div>
                  ))}
                </>
              )}
            </motion.div>
          )}

          {/* Routine Section */}
          {activeSection === 'routine' && (
            <motion.div
              key="routine"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-xl font-bold text-[#30244F] mb-4">Rotina Sugerida</h2>
              
              <div className="relative">
                {/* Timeline Line */}
                <div className="absolute left-[27px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#8F7AC5] via-[#D98DAE] to-[#FAE0D5]" />
                
                {plan?.routine?.map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="relative flex items-center gap-4 mb-4"
                  >
                    <motion.div 
                      className="w-14 h-14 rounded-full bg-white border-2 border-[#8F7AC5] flex items-center justify-center z-10 shadow-sm"
                      whileHover={{ scale: 1.1 }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: index * 0.1, type: "spring" }}
                    >
                      <span className="text-sm font-bold text-[#8F7AC5]">{item.time}</span>
                    </motion.div>
                    <motion.div 
                      className="flex-1 bg-white rounded-xl p-4 shadow-sm border border-[#FAE0D5]"
                      whileHover={{ x: 5 }}
                    >
                      <p className="text-base text-[#30244F] font-medium">{item.activity}</p>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Priorities Section */}
          {activeSection === 'priorities' && (
            <motion.div
              key="priorities"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
              exit={{ opacity: 0, x: -50 }}
            >
              <h2 className="text-xl font-bold text-[#30244F] mb-4">Prioridades por Semana</h2>
              
              {Object.entries(plan?.priorities || {}).map(([week, items], weekIndex) => (
                <motion.div
                  key={week}
                  variants={fadeInUp}
                  className="mb-6"
                >
                  <motion.div 
                    className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] text-white rounded-t-xl px-5 py-3"
                    whileHover={{ scale: 1.02 }}
                  >
                    <h3 className="font-bold text-base flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      {week}
                    </h3>
                  </motion.div>
                  <div className="bg-white rounded-b-xl border-x border-b border-[#FAE0D5] divide-y divide-[#FAE0D5]">
                    {items.map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.1 + weekIndex * 0.2 }}
                        className="px-5 py-4 flex items-start gap-3"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: idx * 0.1 + 0.2, type: "spring" }}
                        >
                          <CheckCircle2 className="w-5 h-5 text-[#7BC47F] mt-0.5" />
                        </motion.div>
                        <span className="text-base text-[#6B5B8C] font-medium">{item}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              ))}

              {/* Expectations */}
              {plan?.expectations?.length > 0 && (
                <motion.div variants={fadeInUp} className="mt-8">
                  <h3 className="text-lg font-bold text-[#30244F] mb-4">Expectativas</h3>
                  <div className="space-y-3">
                    {plan.expectations.map((exp, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.1 }}
                        className="bg-[#FBF4FF] rounded-xl p-4 border border-[#FAE0D5]"
                      >
                        <span className="text-sm font-bold text-[#8F7AC5]">{exp.period}</span>
                        <p className="text-base text-[#6B5B8C] font-medium mt-1">{exp.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Final Message */}
              {plan?.finalMessage && (
                <motion.div
                  variants={fadeInUp}
                  className="mt-8 bg-gradient-to-br from-[#FAE0D5] to-[#FBF4FF] rounded-2xl p-6"
                >
                  <div className="flex items-start gap-4">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Heart className="w-8 h-8 text-[#D98DAE]" />
                    </motion.div>
                    <p className="text-base text-[#6B5B8C] font-medium italic leading-relaxed">
                      {plan.finalMessage}
                    </p>
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Bottom Safe Area */}
      <div className="h-20" />
    </div>
  );
};

export default SharedPlanPage;
